// The odontogram's data layer.
//
// One read (the whole mouth) and one write (a batch of facts). The chart is
// small — 52 teeth — so it is refetched whole after a write rather than
// patched locally: a client-side patch is a second implementation of the
// server's fold, and the two disagreeing is how a dentist ends up looking at a
// tooth state that was never saved.

import React from 'react';
import { bffQuery, bffMutation } from '../lib/bffClient.js';

const TOOTH_STATE_FIELDS = `
  toothFdi
  isPermanent
  isAnterior
  universal
  surfacesAvailable
  presence
  isCharted
  lastRecordedAtMs
  conditions { value material eventId isHistorical atMs note }
  surfaces { surface value material eventId isHistorical atMs }
  notes { eventId text atMs actorId }
`;

export const CHART_QUERY = `
  query DentalChart($patientId: ID!, $clinicId: ID) {
    dentalChart(patientId: $patientId, clinicId: $clinicId) {
      patientId
      summary { chartedTeeth present missing decayed restored dmft }
      teeth { ${TOOTH_STATE_FIELDS} }
    }
  }
`;

export const RECORD_MUTATION = `
  mutation RecordToothEvents($patientId: ID!, $clinicId: ID, $events: [ToothEventInput!]!) {
    recordToothEvents(patientId: $patientId, clinicId: $clinicId, events: $events) {
      id toothFdi aspect surface value material recordedAtMs
    }
  }
`;

export const HISTORY_QUERY = `
  query ToothHistory($patientId: ID!, $toothFdi: String!) {
    toothHistory(patientId: $patientId, toothFdi: $toothFdi) {
      id aspect surface value material note isHistorical
      actorId actorType recordedAtMs occurredAtMs voidedAtMs voidReason
      sourceRecordId
    }
  }
`;

export const VOID_MUTATION = `
  mutation VoidToothEvent($eventId: ID!, $reason: String) {
    voidToothEvent(eventId: $eventId, reason: $reason)
  }
`;

/**
 * ONE tooth's audit trail — every fact ever recorded about it, newest first,
 * INCLUDING the voided entries.
 *
 * dent-visit.md §3.1.1 asks for "clickable teeth for drilling down into
 * specific history/findings", and the chart's folded state answers only the
 * second half: it says a tooth is crowned, not when, by whom, or what was there
 * before. `HISTORY_QUERY` existed here unused until the visit workspace needed
 * the first half.
 *
 * LAZY on purpose. A patient has 52 teeth and this is one read per tooth, so it
 * fires when a tooth is opened and not with the chart.
 */
export function useToothHistory({ patientId, toothFdi, enabled }) {
  const [events, setEvents] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const wanted = React.useRef(toothFdi);
  wanted.current = toothFdi;

  React.useEffect(() => {
    if (!enabled || !patientId || !toothFdi) {
      setEvents([]);
      return undefined;
    }
    let alive = true;
    const asked = toothFdi;
    setLoading(true);
    setError(null);
    bffQuery(HISTORY_QUERY, { patientId, toothFdi: asked })
      .then((data) => {
        if (!alive || wanted.current !== asked) return;
        setEvents(data?.toothHistory || []);
      })
      .catch((err) => {
        if (!alive || wanted.current !== asked) return;
        console.error('[useToothHistory] failed:', err?.message);
        setError(err);
      })
      .finally(() => {
        if (alive && wanted.current === asked) setLoading(false);
      });
    return () => { alive = false; };
  }, [patientId, toothFdi, enabled]);

  return { events, loading, error };
}

export function useDentalChart({ patientId, clinicId }) {
  const [chart, setChart] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  // Guards a slow response for a patient the dentist has already navigated
  // away from — it must never paint over the chart now on screen.
  const wantedRef = React.useRef(patientId);
  wantedRef.current = patientId;

  const load = React.useCallback(async ({ initial } = {}) => {
    if (!patientId) return;
    const asked = patientId;
    if (initial) setLoading(true);
    try {
      const data = await bffQuery(CHART_QUERY, { patientId: asked, clinicId: clinicId || null });
      if (wantedRef.current !== asked) return;
      setChart(data?.dentalChart || null);
      setError(null);
    } catch (err) {
      if (wantedRef.current !== asked) return;
      console.error('[useDentalChart] load failed:', err?.message);
      // A failed BACKGROUND refresh keeps the chart on screen: stale teeth are
      // more use to a dentist mid-treatment than an error card replacing them.
      if (initial) setError(err);
    } finally {
      if (initial && wantedRef.current === asked) setLoading(false);
    }
  }, [patientId, clinicId]);

  React.useEffect(() => { load({ initial: true }); }, [load]);

  /** Record a batch. Resolves to null on success, or the Error on failure, so
   *  the caller can keep the drawer open and show why. */
  const record = React.useCallback(async (events) => {
    if (!events?.length) return null;
    setSaving(true);
    try {
      await bffMutation(RECORD_MUTATION, {
        patientId, clinicId: clinicId || null, events,
      });
      await load();          // refetch: the server owns the fold
      return null;
    } catch (err) {
      console.error('[useDentalChart] record failed:', err?.message);
      return err;
    } finally {
      setSaving(false);
    }
  }, [patientId, clinicId, load]);

  const teeth = chart?.teeth || [];
  const byFdi = React.useMemo(() => {
    const map = {};
    for (const s of teeth) map[s.toothFdi] = s;
    return map;
  }, [teeth]);

  return {
    chart, teeth, byFdi, summary: chart?.summary || null,
    loading, error, saving,
    record, reload: () => load({ initial: true }),
  };
}

export default useDentalChart;
